import { useEffect, useState, type CSSProperties } from 'react'
import { PhoneInput } from 'react-international-phone'
import toast from 'react-hot-toast'
import '../../pages/Booking'
import type { BookingSearchData, TravelerDetails, ArrivalInfo } from '../../pages/Booking'
import type { Vehicle } from '../../types/booking'
import AuthModal from '../../components/AuthModal/AuthModal'
import BookingItineraryCard, {
  type BookingStop,
} from './BookingItineraryCard'
import PickupLocationSelector from './PickupLocationSelector'
import { apiGetUserProfile } from '../../api/profile/userProfileApi'

type BookStep3Props = {
  selectedVehicle: Vehicle | null
  onBack: () => void
  onNext: () => void
  searchData: BookingSearchData
  onSearchDataChange?: (
    next:
      | BookingSearchData
      | ((current: BookingSearchData) => BookingSearchData)
  ) => void
  stops: BookingStop[]
  onEditRoute?: () => void
  travelerDetails: TravelerDetails
  setTravelerDetails: React.Dispatch<React.SetStateAction<TravelerDetails>>
  arrivalInfo: ArrivalInfo
  setArrivalInfo: React.Dispatch<React.SetStateAction<ArrivalInfo>>
  specialRequirements: string
  setSpecialRequirements: React.Dispatch<React.SetStateAction<string>>
  selectedOptions: string[]
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>
  servicePrice?: number
  serviceTitle?: string
  serviceType?: string
}

const PROFILE_STORAGE_KEY = 'salzburg_profile_details'

type LoggedInUserDetails = {
  name: string
  phone: string
  email: string
}

function getLoggedInUserDetails(): LoggedInUserDetails {
  let name = ''
  let phone = ''
  let email = localStorage.getItem('user_email') || ''

  // 1. Try salzburg_profile_details (saved upon login, profile update, Google sign in)
  try {
    const profileStr = localStorage.getItem(PROFILE_STORAGE_KEY)
    if (profileStr) {
      const parsed = JSON.parse(profileStr)
      if (parsed.name && typeof parsed.name === 'string') name = parsed.name.trim()
      if (parsed.phone && typeof parsed.phone === 'string') phone = parsed.phone.trim()
      if (parsed.email && typeof parsed.email === 'string') email = parsed.email.trim()
    }
  } catch {}

  // 2. Try user_data (saved upon API login, auth token exchange, Google sign in)
  try {
    const userDataStr = localStorage.getItem('user_data')
    if (userDataStr) {
      const parsed = JSON.parse(userDataStr)
      if (!name) {
        const fullName = parsed.name || parsed.full_name
        const combined = [parsed.first_name || parsed.firstName, parsed.last_name || parsed.lastName]
          .filter(Boolean)
          .join(' ')
        name = (fullName || combined || '').trim()
      }
      if (!phone) {
        const p = parsed.phone || parsed.phone_number || parsed.phoneNumber || parsed.mobile
        if (typeof p === 'string' && p.trim()) phone = p.trim()
      }
      if (!email && parsed.email) {
        email = parsed.email.trim()
      }
    }
  } catch {}

  // 3. Fallback name from email if name is still empty
  if (!name && email) {
    const local = email.split('@')[0]?.trim() || 'Guest'
    name = local.charAt(0).toUpperCase() + local.slice(1)
  }

  return { name, phone, email }
}

function getLoggedInDisplayName(email: string | null) {
  const details = getLoggedInUserDetails()
  if (details.name) return details.name
  if (!email) return 'Guest'
  const local = email.split('@')[0]?.trim() || 'Guest'
  return local.charAt(0).toUpperCase() + local.slice(1)
}

export type MoreOption = {
  id: string
  label: string
  price?: number
}

export const moreOptions: MoreOption[] = [
  {
    id: 'infant-seat',
    label: 'Infant Seat (0–1 year)',
    price: 5,
  },
  {
    id: 'child-seat',
    label: 'Child Seat (1–3 year)',
    price: 5,
  },
  {
    id: 'booster-seat',
    label: 'Booster Seat (3–12 year)',
  },
  {
    id: 'ski-snowboard',
    label: 'Skis / Snowboard',
  },
]

export default function BookStep3({
  selectedVehicle,
  onBack,
  onNext,
  searchData,
  onSearchDataChange,
  stops,
  onEditRoute,
  travelerDetails,
  setTravelerDetails,
  arrivalInfo,
  setArrivalInfo,
  specialRequirements,
  setSpecialRequirements,
  selectedOptions,
  setSelectedOptions,
  servicePrice,
  serviceTitle,
  serviceType,
}: BookStep3Props) {
  const [userEmail, setUserEmail] = useState<string | null>(() =>
    localStorage.getItem('user_email'),
  )
  const [displayName, setDisplayName] = useState(() =>
    getLoggedInDisplayName(localStorage.getItem('user_email')),
  )

  // const [authOpen, setAuthOpen] = useState(
  //   () => !localStorage.getItem('user_email'),
  // )
  const [authOpen, setAuthOpen] = useState(false)

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)
  const [touched, setTouched] = useState(false)

  const [isArrivalInfoEnabled, setIsArrivalInfoEnabled] = useState(
    () => Boolean(arrivalInfo.number && arrivalInfo.number.trim())
  )

  const handleToggleArrivalInfo = (checked: boolean) => {
    setIsArrivalInfoEnabled(checked)
    if (!checked) {
      setArrivalInfo((prev) => ({
        ...prev,
        number: '',
      }))
    }
  }

  // Validation for Lead traveler fields
  const isNameValid = travelerDetails.name.trim().length >= 2
  const digitsOnly = travelerDetails.phone.replace(/\D/g, '')
  const isPhoneValid = digitsOnly.length >= 6
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(travelerDetails.email.trim())
  const isLeadTravelerComplete = isNameValid && isPhoneValid && isEmailValid

  const otherPersonPhoneDigits =
  travelerDetails.otherPersonPhone.replace(/\D/g, '')

const isOtherPersonValid =
  !travelerDetails.bookingForSomeoneElse ||
  (
    travelerDetails.otherPersonName.trim().length >= 2 &&
    otherPersonPhoneDigits.length >= 6
  )

const handleNextClick = () => {
  setTouched(true)

  if (!isLeadTravelerComplete) {
    toast.error('Please complete all Lead traveler fields.')
    return
  }

  if (!isOtherPersonValid) {
    toast.error('Please enter the name and phone number of the other person.')
    return
  }

  onNext()
}

  // 1. Synchronize details when userEmail changes
  useEffect(() => {
    if (!userEmail) {
      return
    }

    const { name, phone, email } = getLoggedInUserDetails()
    if (name) {
      setDisplayName(name)
    }

    setTravelerDetails((prev) => {
      const nextName = prev.name.trim() ? prev.name : name
      const hasCustomPhone = prev.phone && prev.phone !== '+43' && prev.phone.trim().length > 3
      const nextPhone = hasCustomPhone ? prev.phone : (phone || prev.phone || '+43')
      const nextEmail = prev.email.trim() ? prev.email : (email || userEmail)

      return {
        ...prev,
        name: nextName,
        phone: nextPhone,
        email: nextEmail,
      }
    })

    // Also fetch fresh user profile from API in case it's on the server
    apiGetUserProfile()
      .then((res) => {
        if (res.success && res.data) {
          const profile = res.data
          if (profile.name) setDisplayName(profile.name)
          setTravelerDetails((prev) => {
            const nextName = prev.name.trim() ? prev.name : profile.name
            const hasCustomPhone = prev.phone && prev.phone !== '+43' && prev.phone.trim().length > 3
            const nextPhone = hasCustomPhone ? prev.phone : (profile.phone || prev.phone || '+43')
            const nextEmail = prev.email.trim() ? prev.email : (profile.email || userEmail)
            return {
              ...prev,
              name: nextName,
              phone: nextPhone,
              email: nextEmail,
            }
          })
        }
      })
      .catch(() => {})
  }, [userEmail, setTravelerDetails])

  // 2. Listen for customer profile updates (e.g. from Google Sign-In or profile updates)
  useEffect(() => {
    const handleProfileUpdate = () => {
      const currentEmail = localStorage.getItem('user_email')
      if (currentEmail) {
        setUserEmail(currentEmail)
        const { name, phone, email } = getLoggedInUserDetails()
        if (name) setDisplayName(name)

        setTravelerDetails((prev) => {
          const nextName = prev.name.trim() ? prev.name : name
          const hasCustomPhone = prev.phone && prev.phone !== '+43' && prev.phone.trim().length > 3
          const nextPhone = hasCustomPhone ? prev.phone : (phone || prev.phone || '+43')
          const nextEmail = prev.email.trim() ? prev.email : (email || currentEmail)

          return {
            ...prev,
            name: nextName,
            phone: nextPhone,
            email: nextEmail,
          }
        })
      }
    }

    window.addEventListener('customer-profile-updated', handleProfileUpdate)
    window.addEventListener('storage', handleProfileUpdate)
    return () => {
      window.removeEventListener('customer-profile-updated', handleProfileUpdate)
      window.removeEventListener('storage', handleProfileUpdate)
    }
  }, [setTravelerDetails])

  const [requirementsOpen, setRequirementsOpen] = useState(false)

  // const optionsPrice = selectedOptions.reduce((total, optionId) => {
  //   const option = moreOptions.find((item) => item.id === optionId)
  //   return total + (option?.price ?? 0)
  // }, 0)

  
  const optionsPrice = selectedOptions.reduce((total, optionId) => {
    const option = moreOptions.find((item) => item.id === optionId)
    return total + (option?.price ?? 0)
  }, 0) * (searchData.returnDate ? 2 : 1)


  const toggleOption = (optionId: string) => {
    setSelectedOptions((currentOptions) =>
      currentOptions.includes(optionId)
        ? currentOptions.filter((id) => id !== optionId)
        : [...currentOptions, optionId],
    )
  }

  const handleLoginSuccess = (loggedInEmail: string) => {
    setUserEmail(loggedInEmail)
    setAuthOpen(false)
    const { name, phone, email } = getLoggedInUserDetails()
    if (name) setDisplayName(name)
    setTravelerDetails((prev) => ({
      ...prev,
      name: name || prev.name,
      phone: (phone && phone.trim()) ? phone : (prev.phone || '+43'),
      email: email || loggedInEmail || prev.email,
    }))
  }

  const confirmLogout = () => {
    localStorage.removeItem('user_email')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
    localStorage.removeItem(PROFILE_STORAGE_KEY)
    setUserEmail(null)
    setDisplayName('Guest')
    setTravelerDetails((prev) => ({ ...prev, name: '', email: '', phone: '+43' }))
    setLogoutConfirmOpen(false)
    // setAuthOpen(true)
  }

  return (
    <section className="relative px-4 pb-[150px] sm:px-8 lg:px-[6vw]">
      <div className="mx-auto w-full max-w-[1450px]">
        <div className="mt-8">
          <h1 className="font-host text-[22px] font-semibold tracking-[-0.03em] text-black sm:text-[30px]">
            Traveler Details
          </h1>
          <p className="mt-2 font-poppins text-[14px] leading-[1.55] text-[#333333] sm:text-[16px]">
            Provide your contact information and arrival details for a smooth pickup.
          </p>
        </div>

        <div className="mt-7 grid items-start gap-8 lg:grid-cols-[minmax(0,1.34fr)_minmax(410px,0.86fr)] lg:gap-10">
          <div className="space-y-5">
            {userEmail ? (
              <div className="flex min-h-[60px] flex-col gap-3 rounded-[22px] bg-white px-4 py-4 shadow-[0_5px_18px_rgba(0,0,0,0.07)] sm:flex-row sm:items-center sm:justify-between sm:px-7">
                <p className="min-w-0 font-poppins text-[14px] font-medium text-[#333333] sm:text-[15px]">
                  You are logged in as {displayName}.
                </p>

                <button
                  type="button"
                  onClick={() => setLogoutConfirmOpen(true)}
                  className="shrink-0 self-start py-1 font-poppins text-[12px] font-medium text-[#2459A2] sm:self-auto"
                >
                  Log Out
                </button>
              </div>
            ) : (
              <div className="flex min-h-[60px] flex-col gap-3 rounded-[22px] bg-white px-4 py-4 shadow-[0_5px_18px_rgba(0,0,0,0.07)] sm:flex-row sm:items-center sm:justify-between sm:px-7">
                <p className="min-w-0 font-poppins text-[13px] font-medium text-[#333333] sm:text-[14px]">
                  Continue as a guest, or sign in to use your saved details.
                </p>

                <div className="flex items-center gap-3 shrink-0 self-start sm:self-auto">
                  <button
                    type="button"
                    onClick={() => setAuthOpen(true)}
                    className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-1.5 font-poppins text-[12px] font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition active:scale-[0.98]"
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v3.9h6.6c-.28 1.5-1.11 2.76-2.39 3.62v3h3.86c2.26-2.09 3.56-5.16 3.56-8.75Z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A11.97 11.97 0 0 0 12 24Z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.27 14.29a7.18 7.18 0 0 1 0-4.58V6.6H1.29a11.94 11.94 0 0 0 0 10.8l3.98-3.11Z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0A11.96 11.96 0 0 0 1.29 6.6l3.98 3.1c.95-2.85 3.6-4.95 6.73-4.95Z"
                      />
                    </svg>
                    Sign in with Google
                  </button>

                  <button
                    type="button"
                    onClick={() => setAuthOpen(true)}
                    className="shrink-0 py-1 font-poppins text-[12px] font-medium text-[#2459A2] hover:underline"
                  >
                    Sign In
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-[22px] bg-white shadow-[0_5px_18px_rgba(0,0,0,0.09)]">
              <div className="flex min-h-[68px] flex-wrap items-center justify-between gap-2 border-b border-[#DDDDDD] px-4 py-4 sm:px-7 sm:py-5">
                <h2 className="font-host text-[17px] font-semibold text-black sm:text-[18px]">
                  Lead traveler
                </h2>

                <span className="font-poppins text-[11px] text-[#858585] sm:text-[12px]">
                  Adult (over 18)
                </span>
              </div>

              <div className="space-y-3 px-4 pb-5 pt-4 sm:px-7 sm:pb-7">
                <label className={`block min-h-[64px] rounded-[12px] px-5 py-3 border transition ${
                  touched && !isNameValid ? 'border-red-400 bg-red-50/20' : 'border-transparent bg-[#EEEEEE]'
                }`}>
                  <span className="block font-poppins text-[10px] text-[#222222]">
                    Name <span className="text-red-500">*</span>
                  </span>

                  <input
                    type="text"
                    placeholder="Enter full name"
                    value={travelerDetails.name}
                    onChange={(event) =>
                      setTravelerDetails((prev) => ({ ...prev, name: event.target.value }))
                    }
                    className="mt-1 w-full bg-transparent font-poppins text-[14px] font-medium text-black outline-none placeholder:text-gray-400"
                  />
                </label>
                {touched && !isNameValid && (
                  <p className="text-[11px] font-poppins text-red-500 pl-2 -mt-1">
                    Please enter the traveler's full name.
                  </p>
                )}

                <div className={`min-h-[60px] overflow-visible rounded-[12px] border transition ${
                  touched && !isPhoneValid ? 'border-red-400 bg-red-50/20' : 'border-transparent bg-[#EEEEEE]'
                }`}>
                  <PhoneInput
                    defaultCountry="at"
                    preferredCountries={['at', 'in', 'gb', 'us']}
                    value={travelerDetails.phone}
                    onChange={(phone) => setTravelerDetails((prev) => ({ ...prev, phone }))}
                    forceDialCode
                    placeholder="Phone Number"
                    className="w-full"
                    style={
                      {
                        '--react-international-phone-height': '60px',
                        '--react-international-phone-background-color':
                          'transparent',
                        '--react-international-phone-text-color': '#000000',
                        '--react-international-phone-font-size': '13px',
                        '--react-international-phone-border-color':
                          'transparent',
                        '--react-international-phone-border-radius': '12px',
                      } as CSSProperties
                    }
                    inputClassName="!w-full !border-0 !bg-transparent !px-4 !font-poppins !text-[12px] !font-medium !text-black !outline-none"
                    countrySelectorStyleProps={{
                      buttonClassName:
                        '!h-[60px] !min-w-[112px] !rounded-l-[12px] !border-0 !border-r !border-white !bg-transparent !px-4',
                      buttonContentWrapperClassName:
                        '!gap-2 !font-poppins !text-[12px] !font-medium',
                      flagClassName: '!h-[22px] !w-[22px]',
                      dropdownStyleProps: {
                        className:
                          '!z-[200] !max-h-[300px] !w-[min(320px,calc(100vw-2rem))] !rounded-[12px] !border !border-[#D8D8D8] !bg-white !shadow-[0_10px_30px_rgba(0,0,0,0.16)]',
                      },
                    }}
                    inputProps={{
                      name: 'phoneNumber',
                      autoComplete: 'tel',
                      'aria-label': 'Phone number',
                    }}
                  />
                </div>
                {touched && !isPhoneValid && (
                  <p className="text-[11px] font-poppins text-red-500 pl-2 -mt-1">
                    Please enter a valid phone number with country code.
                  </p>
                )}

                <label className={`block min-h-[60px] rounded-[12px] px-5 py-3 border transition ${
                  touched && !isEmailValid ? 'border-red-400 bg-red-50/20' : 'border-transparent bg-[#EEEEEE]'
                }`}>
                  <span className="block font-poppins text-[10px] text-[#9D9D9D]">
                    Email <span className="text-red-500">*</span>
                  </span>

                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={travelerDetails.email}
                    onChange={(event) => setTravelerDetails((prev) => ({ ...prev, email: event.target.value }))}
                    className="mt-1 w-full bg-transparent font-poppins text-[13px] font-medium text-black outline-none placeholder:text-gray-400"
                  />
                </label>
                {touched && !isEmailValid && (
                  <p className="text-[11px] font-poppins text-red-500 pl-2 -mt-1">
                    Please enter a valid email address.
                  </p>
                )}

               <label className="flex cursor-pointer items-center gap-3 pt-3">
                <input
                  type="checkbox"
                  checked={travelerDetails.bookingForSomeoneElse}
                  onChange={(event) =>
                    setTravelerDetails((prev) => ({
                      ...prev,
                      bookingForSomeoneElse: event.target.checked,
                    }))
                  }
                  className="sr-only"
                />

                <span
                  className={`flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[2px] border transition-colors ${
                    travelerDetails.bookingForSomeoneElse
                      ? 'border-[#9BC344] bg-[#9BC344] text-white'
                      : 'border-[#777777] bg-white'
                  }`}
                >
                  {travelerDetails.bookingForSomeoneElse && (
                    <svg
                      className="h-3.5 w-3.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>

                <span className="font-poppins text-[14px] text-[#333333]">
                  Book for someone else
                </span>
              </label>


                {travelerDetails.bookingForSomeoneElse && (
              <div className="mt-4 space-y-3">
                <label className="block min-h-[60px] rounded-[12px] bg-[#EEEEEE] px-5 py-3">
                  <span className="block font-poppins text-[10px] text-[#9D9D9D]">
                    Name <span className="text-red-500">*</span>
                  </span>

                  <input
                    type="text"
                    placeholder="Enter Leading Passenger's name"
                    value={travelerDetails.otherPersonName}
                    onChange={(event) =>
                      setTravelerDetails((prev) => ({
                        ...prev,
                        otherPersonName: event.target.value,
                      }))
                    }
                    className="mt-1 w-full bg-transparent font-poppins text-[13px] font-medium text-black outline-none placeholder:text-gray-400"
                  />
                </label>

   <div className="min-h-[60px] overflow-visible rounded-[12px] bg-[#EEEEEE]">
  <PhoneInput
    defaultCountry="at"
    preferredCountries={['at', 'in', 'gb', 'us']}
    value={travelerDetails.otherPersonPhone}
    onChange={(phone) =>
      setTravelerDetails((prev) => ({
        ...prev,
        otherPersonPhone: phone,
      }))
    }
    forceDialCode
    placeholder="Phone Number"
    className="w-full"
    style={
      {
        '--react-international-phone-height': '60px',
        '--react-international-phone-background-color': 'transparent',
        '--react-international-phone-text-color': '#000000',
        '--react-international-phone-font-size': '13px',
        '--react-international-phone-border-color': 'transparent',
        '--react-international-phone-border-radius': '12px',
      } as CSSProperties
    }
    inputClassName="!w-full !border-0 !bg-transparent !px-4 !font-poppins !text-[12px] !font-medium !text-black !outline-none"
    countrySelectorStyleProps={{
      buttonClassName:
        '!h-[60px] !min-w-[112px] !rounded-l-[12px] !border-0 !border-r !border-white !bg-transparent !px-4',
      buttonContentWrapperClassName:
        '!gap-2 !font-poppins !text-[12px] !font-medium',
      flagClassName: '!h-[22px] !w-[22px]',
      dropdownStyleProps: {
        className:
          '!z-[200] !max-h-[300px] !w-[min(320px,calc(100vw-2rem))] !rounded-[12px] !border !border-[#D8D8D8] !bg-white !shadow-[0_10px_30px_rgba(0,0,0,0.16)]',
      },
    }}
    inputProps={{
      name: 'otherPersonPhone',
      autoComplete: 'tel',
      'aria-label': 'Other person phone number',
    }}
  />
</div>
  </div>
)}
              </div>
            </div>

            {/* Pickup Location Selector (Free Current Location or Manual) */}
            <PickupLocationSelector
              fromTitle={searchData.fromTitle}
              fromAddress={searchData.fromAddress}
              onLocationChange={({ fromTitle, fromAddress, position, keyword }) => {
                if (onSearchDataChange) {
                  onSearchDataChange((prev) => ({
                    ...prev,
                    fromTitle,
                    fromAddress,
                    position: position || prev.position,
                    keyword: keyword || fromTitle,
                  }))
                }
              }}
            />

            {/* Arrival Information (Flight / Train) - Optional */}
            <div className="overflow-hidden rounded-[22px] bg-white shadow-[0_5px_18px_rgba(0,0,0,0.09)] border border-[#ECECEC] transition-all">
              {/* Header with Optional Toggle Checkbox */}
              <div
                className={`px-4 py-4 sm:px-7 sm:py-5 transition-colors ${
                  isArrivalInfoEnabled ? 'border-b border-[#EAEAEA] bg-white' : 'hover:bg-[#FCFCFC]'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <label className="flex items-center gap-3.5 cursor-pointer select-none">
                    <div
                      onClick={(e) => {
                        e.preventDefault()
                        handleToggleArrivalInfo(!isArrivalInfoEnabled)
                      }}
                      className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[5px] border transition-colors ${
                        isArrivalInfoEnabled
                          ? 'bg-[#9BC344] border-[#9BC344] text-white shadow-sm'
                          : 'border-[#777777] bg-white hover:border-[#9BC344]'
                      }`}
                    >
                      {isArrivalInfoEnabled && (
                        <svg
                          className="h-3.5 w-3.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={isArrivalInfoEnabled}
                      onChange={(e) => handleToggleArrivalInfo(e.target.checked)}
                      className="sr-only"
                    />
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EBF3FB] text-[#365E92] shrink-0">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="font-host text-[18px] font-semibold text-black sm:text-[20px]">
                          Arrival Information
                        </h2>
                        <span className="rounded-full bg-[#F3F4F6] border border-[#E5E7EB] px-2.5 py-0.5 font-poppins text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">
                          Optional
                        </span>
                      </div>
                      <p className="font-poppins text-[11px] text-[#777777] mt-0.5">
                        {isArrivalInfoEnabled
                          ? 'Provide flight or train details for airport/station delay tracking and pickup coordination'
                          : 'Check this box if you are arriving by flight or train at airport or station'}
                      </p>
                    </div>
                  </label>

                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF4FB] px-3 py-1 font-poppins text-[11px] font-semibold text-[#29507E] border border-[#D0E2F5]">
                      <span className="h-2 w-2 rounded-full bg-[#365E92] animate-pulse" />
                      FLIGHT & TRAIN TRACKING
                    </span>
                  </div>
                </div>
              </div>

              {/* Body: Visible only when enabled */}
              {isArrivalInfoEnabled && (
                <div>
                  <div className="border-b border-[#DDDDDD] px-4 pb-4 pt-5 sm:px-7 sm:pt-6">
                    <div className="flex items-center justify-between">
                      <span className="font-host text-[14px] font-semibold text-[#333333]">
                        Select Arrival Mode
                      </span>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            setArrivalInfo((prev) => ({
                              ...prev,
                              type: 'flight',
                              airlineOrOperator:
                                prev.airlineOrOperator === 'Search Operator'
                                  ? 'Search Airline'
                                  : prev.airlineOrOperator,
                            }))
                          }
                          aria-label="Flight arrival"
                          className={`flex h-[44px] w-[44px] items-center justify-center rounded-[10px] border transition-colors ${
                            arrivalInfo.type === 'flight'
                              ? 'border-[#365E92] bg-[#365E92]'
                              : 'border-[#365E92] bg-white'
                          }`}
                        >
                          <img
                            src="/assets/images/flight.png"
                            alt=""
                            className="h-[30px] w-[30px] object-contain"
                          />
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            setArrivalInfo((prev) => ({
                              ...prev,
                              type: 'train',
                              airlineOrOperator:
                                prev.airlineOrOperator === 'Search Airline'
                                  ? 'Search Operator'
                                  : prev.airlineOrOperator,
                            }))
                          }
                          aria-label="Train arrival"
                          className={`flex h-[44px] w-[44px] items-center justify-center rounded-[10px] border transition-colors ${
                            arrivalInfo.type === 'train'
                              ? 'border-[#365E92] bg-[#365E92]'
                              : 'border-[#365E92] bg-white'
                          }`}
                        >
                          <img
                            src="/assets/images/train.png"
                            alt=""
                            className="h-[30px] w-[30px] object-contain"
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 px-4 pb-6 pt-4 sm:grid-cols-2 sm:px-7 sm:pb-8">
                    <label className="min-h-[60px] rounded-[12px] bg-[#EEEEEE] px-5 py-3">
                      <span className="block font-poppins text-[10px] text-[#888888]">
                        {arrivalInfo.type === 'flight'
                          ? 'Airline'
                          : 'Train Operator'}
                      </span>

                      <select
                        value={arrivalInfo.airlineOrOperator}
                        onChange={(event) =>
                          setArrivalInfo((prev) => ({
                            ...prev,
                            airlineOrOperator: event.target.value,
                          }))
                        }
                        className="mt-1 w-full bg-transparent font-poppins text-[13px] font-medium text-black outline-none"
                      >
                        <option value={arrivalInfo.type === 'flight' ? 'Search Airline' : 'Search Operator'}>
                          Search {arrivalInfo.type === 'flight' ? 'Airline' : 'Operator'}
                        </option>
                        {arrivalInfo.type === 'flight' ? (
                          <>
                            <option value="Austrian Airlines">Austrian Airlines</option>
                            <option value="Lufthansa">Lufthansa</option>
                            <option value="Emirates">Emirates</option>
                            <option value="British Airways">British Airways</option>
                            <option value="Swiss International Air Lines">Swiss International Air Lines</option>
                            <option value="Eurowings">Eurowings</option>
                            <option value="Ryanair">Ryanair</option>
                            <option value="Turkish Airlines">Turkish Airlines</option>
                            <option value="KLM Royal Dutch Airlines">KLM Royal Dutch Airlines</option>
                            <option value="EasyJet">EasyJet</option>
                            <option value="Others">Others</option>
                          </>
                        ) : (
                          <>
                            <option value="ÖBB (Austrian Federal Railways)">ÖBB (Austrian Federal Railways)</option>
                            <option value="Westbahn">Westbahn</option>
                            <option value="Deutsche Bahn (DB)">Deutsche Bahn (DB)</option>
                            <option value="Railjet">Railjet</option>
                            <option value="Nightjet">Nightjet</option>
                            <option value="RegioJet">RegioJet</option>
                             <option value="Others">Others</option>
                          </>
                        )}
                      </select>
                    </label>

                    <label className="min-h-[60px] rounded-[12px] bg-[#EEEEEE] px-5 py-3">
                      <span className="block font-poppins text-[10px] text-[#888888]">
                        {arrivalInfo.type === 'flight'
                          ? 'Flight Number'
                          : 'Train Number'}
                      </span>

                      <input
                        type="text"
                        value={arrivalInfo.number}
                        onChange={(event) =>
                          setArrivalInfo((prev) => ({
                            ...prev,
                            number: event.target.value,
                          }))
                        }
                        placeholder={arrivalInfo.type === 'flight' ? 'e.g. OS 452' : 'e.g. RJ 123'}
                        className="mt-1 w-full bg-transparent font-poppins text-[13px] font-medium text-[#555555] outline-none"
                      />
                    </label>

                    <label className="min-h-[60px] rounded-[12px] bg-[#EEEEEE] px-5 py-3">
                     <span className="block font-poppins text-[10px] text-[#888888]">
                        {arrivalInfo.type === 'flight'
                          ? 'Arrival Terminal'
                          : 'Arrival Platform'}
                      </span>

                      <select
                        value={arrivalInfo.terminal}
                        onChange={(event) =>
                          setArrivalInfo((prev) => ({
                            ...prev,
                            terminal: event.target.value,
                          }))
                        }
                        className="mt-1 w-full bg-transparent font-poppins text-[13px] font-medium text-black outline-none"
                      >
                       {arrivalInfo.type === 'flight' ? (
                            <>
                              <option value="Terminal 1">Terminal 1</option>
                              <option value="Terminal 2">Terminal 2</option>
                              <option value="Terminal 3">Terminal 3</option>
                            </>
                          ) : (
                            <>
                              <option value="Platform 1">Platform 1</option>
                              <option value="Platform 2">Platform 2</option>
                              <option value="Platform 3">Platform 3</option>
                              <option value="Main Concourse">Main Concourse</option>
                            </>
                          )}
                      </select>
                    </label>

                    <label className="min-h-[60px] rounded-[12px] bg-[#EEEEEE] px-5 py-3">
                      <span className="block font-poppins text-[10px] text-[#888888]">
                        Scheduled Arrival Time
                      </span>

                      <input
                        type="time"
                        value={arrivalInfo.time}
                        onChange={(event) =>
                          setArrivalInfo((prev) => ({
                            ...prev,
                            time: event.target.value,
                          }))
                        }
                        className="mt-1 w-full bg-transparent font-poppins text-[13px] font-medium text-black outline-none"
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-[22px] bg-white px-4 py-5 shadow-[0_5px_18px_rgba(0,0,0,0.09)] sm:px-7">
              <h2 className="font-host text-[19px] font-semibold text-black">
                More Options
              </h2>

              <div className="mt-5 space-y-3">
                {moreOptions.map((option) => (
                  <label
                    key={option.id}
                    className="flex cursor-pointer items-center gap-3"
                  >
                    <span className="relative flex h-[26px] w-[26px] shrink-0 items-center justify-center">
                      <input
                        type="checkbox"
                        checked={selectedOptions.includes(option.id)}
                        onChange={() => toggleOption(option.id)}
                        className="peer h-[26px] w-[26px] cursor-pointer appearance-none rounded-[6px] border border-[#8E8E8E] bg-white transition-colors checked:border-[#9BC344] checked:bg-[#9BC344]"
                      />

                      <svg
                        className="pointer-events-none absolute h-[16px] w-[16px] text-white opacity-0 transition-opacity peer-checked:opacity-100"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>

                    <span className="font-poppins text-[16px] text-[#333333]">
                      {option.label}
                      {option.price !== undefined && (
                        <span className="ml-3">€{option.price}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="overflow-hidden rounded-[14px] bg-white shadow-[0_5px_18px_rgba(0,0,0,0.09)]">
              <button
                type="button"
                onClick={() =>
                  setRequirementsOpen((current) => !current)
                }
                className="flex min-h-[64px] w-full items-center justify-between gap-3 px-4 py-4 sm:px-7"
              >
                <span className="flex items-center gap-3 font-poppins text-[14px] font-semibold text-[#333333]">
                  <img
                    src="/assets/images/message.png"
                    alt=""
                    className="h-[23px] w-[23px] object-contain"
                  />
                  I have a special requirements
                </span>

                <span className="text-[34px] font-light leading-none text-black">
                  {requirementsOpen ? '−' : '+'}
                </span>
              </button>

              {requirementsOpen && (
                <div className="border-t border-[#EEEEEE] px-4 py-5 sm:px-7">
                  <textarea
                    value={specialRequirements}
                    onChange={(event) =>
                      setSpecialRequirements(event.target.value)
                    }
                    rows={4}
                    placeholder="Enter your special requirements"
                    className="w-full resize-none rounded-[10px] border border-[#D8D8D8] px-4 py-3 font-poppins text-[12px] text-black outline-none focus:border-[#9BC344]"
                  />
                </div>
              )}
            </div>
          </div>

          <BookingItineraryCard
            searchData={searchData}
            selectedVehicle={selectedVehicle}
            stops={stops}
            extraPrice={optionsPrice}
            extraPriceLabel="Additional Options"
            onEditRoute={onEditRoute}
            onSearchDataChange={onSearchDataChange}
            specialRequirements={specialRequirements}
            arrivalInfo={isArrivalInfoEnabled ? arrivalInfo : undefined}
            servicePrice={servicePrice}
            serviceTitle={serviceTitle}
            serviceType={serviceType}
          />
        </div>
      </div>

      <div className="fixed bottom-3 left-1/2 z-[100] w-[calc(100%-1.5rem)] -translate-x-1/2 rounded-[18px] border border-[#ECECEC] bg-white/95 px-3 py-3 shadow-[0_5px_22px_rgba(0,0,0,0.14)] backdrop-blur-md sm:bottom-4 sm:w-[88vw] sm:px-10 sm:py-5 lg:w-[68vw] lg:max-w-[1100px]">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <button
            type="button"
            onClick={onBack}
            className="flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-full bg-black px-4 font-poppins text-[12px] font-medium text-white sm:min-h-[54px] sm:min-w-[190px] sm:flex-none sm:px-7"
          >
            Back
          </button>

          <button
            type="button"
            onClick={handleNextClick}
            className={`flex min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-full px-4 font-poppins text-[12px] font-medium text-white transition-all duration-200 sm:min-h-[54px] sm:min-w-[190px] sm:flex-none sm:px-7 ${
              isLeadTravelerComplete
                ? 'bg-[#FF6B2C] hover:-translate-y-0.5 shadow-md active:scale-95 cursor-pointer'
                : 'bg-[#FF6B2C]/65 opacity-85 cursor-not-allowed hover:bg-[#FF6B2C]/75'
            }`}
          >
            Next: Payment
          </button>
        </div>
      </div>

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {logoutConfirmOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-[380px] rounded-[18px] bg-white p-6 shadow-[0_8px_30px_rgba(0,0,0,0.18)]">
            <h3 className="font-host text-[16px] font-semibold text-black">
              Log out?
            </h3>

            <p className="mt-2 font-poppins text-[12px] text-[#555555]">
              You will need to sign in again to continue with your traveler
              details.
            </p>

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setLogoutConfirmOpen(false)}
                className="rounded-full bg-[#F1F1F1] px-5 py-2.5 font-poppins text-[11px] font-medium text-black"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmLogout}
                className="rounded-full bg-[#FF6B2C] px-5 py-2.5 font-poppins text-[11px] font-medium text-white"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}